import { useEffect, useRef } from "react";
import { Button, Separator } from "@/components/ui";
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Link,
  Image,
  Minus,
  Plus,
} from "lucide-react";
import { RichTextEditorProps } from "./types";
import { cn } from "@/lib/utils";

const RichTextEditor = ({ value, onChange, disabled }: RichTextEditorProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const isInternalUpdate = useRef(false);

  /** Get iframe document */
  const getDoc = () =>
    iframeRef.current?.contentDocument ||
    iframeRef.current?.contentWindow?.document;

  /** Execute command inside iframe */
  const exec = (command: string, val?: string) => {
    const doc = getDoc();
    if (!doc) return;
    doc.body.focus();
    doc.execCommand(command, false, val);
  };

  /** Initialize iframe once */
  useEffect(() => {
    const doc = getDoc();
    if (!doc) return;

    doc.open();
    doc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <style>
            body {
              font-family: system-ui, sans-serif;
              padding: 12px;
              font-size: 14px;
              outline: none;
              overflow-y: auto;
            }
            img {
              max-width: 100%;
            }
          </style>
        </head>
        <body></body>
      </html>
    `);
    doc.close();
  }, []);

  useEffect(() => {
    const doc = getDoc();
    if (!doc || !doc.body) return;

    doc.body.contentEditable = (!disabled).toString();
  }, [disabled]);

  useEffect(() => {
    const doc = getDoc();
    if (!doc || !doc.body) return;

    doc.body.oninput = () => {
      if (disabled) return;
      isInternalUpdate.current = true;
      onChange?.(doc.body.innerHTML);
    };
  }, [disabled]);

  /** Sync external value → iframe */
  useEffect(() => {
    const doc = getDoc();
    if (!doc || !doc.body) return;

    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }

    doc.body.innerHTML = value || "";
  }, [value]);

  return (
    <div className="rounded-xl border bg-white w-full">
      {/* Toolbar */}
      {!disabled && (
        <>
          <div className="flex flex-wrap items-center gap-1 px-2 py-2">
            <Button size="icon" variant="ghost" onClick={() => exec("bold")}>
              <Bold size={16} />
            </Button>
            <Button size="icon" variant="ghost" onClick={() => exec("italic")}>
              <Italic size={16} />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => exec("underline")}
            >
              <Underline size={16} />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => exec("strikeThrough")}
            >
              <Strikethrough size={16} />
            </Button>

            <Separator orientation="vertical" className="mx-1 h-5" />

            <Button
              size="icon"
              variant="ghost"
              onClick={() => exec("justifyLeft")}
            >
              <AlignLeft size={16} />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => exec("justifyCenter")}
            >
              <AlignCenter size={16} />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => exec("justifyRight")}
            >
              <AlignRight size={16} />
            </Button>

            <Separator orientation="vertical" className="mx-1 h-5" />

            <Button
              size="icon"
              variant="ghost"
              onClick={() => {
                const url = prompt("Enter link");
                if (url) exec("createLink", url);
              }}
            >
              <Link size={16} />
            </Button>

            <Button
              size="icon"
              variant="ghost"
              onClick={() => {
                const url = prompt("Image URL");
                if (url) exec("insertImage", url);
              }}
            >
              <Image size={16} />
            </Button>

            <Separator orientation="vertical" className="mx-1 h-5" />

            <Button
              size="icon"
              variant="ghost"
              onClick={() => exec("decreaseFontSize")}
            >
              <Minus size={14} />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => exec("increaseFontSize")}
            >
              <Plus size={14} />
            </Button>
          </div>

          <Separator />
        </>
      )}

      {/* Editor iframe */}
      <iframe
        ref={iframeRef}
        className={cn(
          "w-full min-h-[260px] max-h-[300px] border-0",
          disabled && "opacity-60"
        )}
      />
    </div>
  );
};

export default RichTextEditor;
